import React, { useState, useEffect, useMemo } from "react";
import "./AdminWeeklyExport.css"; 
import bfLogo from "../assets/bf_logo.png"; 
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Download, ChevronLeft, ChevronRight, ArrowLeft } from "lucide-react";
import { db, auth } from "../firebase"; 
import { doc, getDoc, onSnapshot, query, collection, where, getDocs } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";

// PDF BIBLIOTEKOS
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const DAYS_KEYS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

const latinize = (str) => {
  if (!str) return "";
  return str.replace(/[ąĄ]/g, 'a').replace(/[čČ]/g, 'c').replace(/[ęĘ]/g, 'e')
    .replace(/[ėĖ]/g, 'e').replace(/[įĮ]/g, 'i').replace(/[šŠ]/g, 's')
    .replace(/[ųŲ]/g, 'u').replace(/[ūŪ]/g, 'u').replace(/[žŽ]/g, 'z')
    .replace(/[üÜ]/g, 'ue').replace(/[äÄ]/g, 'ae').replace(/[öÖ]/g, 'oe')
    .replace(/[ß]/g, 'ss');
};

const getPastelRgbArray = (str) => {
  if (!str) return null;
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  const h = Math.abs(hash % 360) / 360;
  const s = (60 + (Math.abs(hash) % 20)) / 100; 
  const l = (85 + (Math.abs(hash) % 10)) / 100;
  let r, g, b;
  if (s === 0) { r = g = b = l; } else {
    const hue2rgb = (p, q, t) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }
  return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
};

const hexToRgbArray = (hex) => {
    if (!hex) return null;
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? [
        parseInt(result[1], 16),
        parseInt(result[2], 16),
        parseInt(result[3], 16)
    ] : null;
};

const getDatesForKW = (kw, year) => {
  const simple = new Date(year, 0, 1 + (kw - 1) * 7);
  const ISOweekStart = new Date(simple);
  const dow = simple.getDay();
  if (dow <= 4) ISOweekStart.setDate(simple.getDate() - simple.getDay() + 1);
  else ISOweekStart.setDate(simple.getDate() + 8 - simple.getDay());
  
  return DAYS_KEYS.map((_, i) => {
    const d = new Date(ISOweekStart);
    d.setDate(ISOweekStart.getDate() + i);
    return {
        dateObj: d,
        formatted: d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" })
    };
  });
};

const getCurrentISOWeek = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 4 - (d.getDay() || 7));
  const yearStart = new Date(d.getFullYear(), 0, 1);
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
};

export default function AdminWeeklyExport() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  
  const urlKw = searchParams.get("kw");
  const urlYear = searchParams.get("year");
  
  const [kw, setKw] = useState(urlKw ? parseInt(urlKw) : getCurrentISOWeek());
  const [year] = useState(urlYear ? parseInt(urlYear) : new Date().getFullYear());
  
  const [allWorkers, setAllWorkers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [statuses, setStatuses] = useState({});
  const [detailedWorkerData, setDetailedWorkerData] = useState({}); 
  const [fullCompanyInfo, setFullCompanyInfo] = useState({ name: "", address: "", admin: "" });
  const [currentUser, setCurrentUser] = useState(null);

  const weekData = useMemo(() => getDatesForKW(kw, year), [kw, year]);
  const dateRange = `${weekData[0].formatted} - ${weekData[6].formatted}.${year}`;

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) setCurrentUser(user);
      else navigate("/admin/login");
    });
    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    if (!currentUser) return;
    const loadData = async () => {
      const userRef = doc(db, "users", currentUser.uid);
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        const d = userSnap.data();
        const addrObj = d.address || {};
        setFullCompanyInfo({
          name: d.companyName || "BuildForce",
          address: `${addrObj.street || ""}, ${addrObj.zip || ""} ${addrObj.city || ""}`.trim(),
          admin: d.name || ""
        });
        const masterId = d.belongsTo || currentUser.uid;
        
        // 1. Darbuotojai
        const qWorkers = query(collection(db, "workers"), where("adminId", "==", masterId));
        const workerSnap = await getDocs(qWorkers);
        const workersList = workerSnap.docs
            .map(doc => ({ id: doc.id, ...doc.data() }))
            .filter(w => !w.archived)
            .sort((a, b) => a.name.localeCompare(b.name));
        setAllWorkers(workersList);

        // 2. Projektai
        const qProjects = query(collection(db, "projects"), 
          where("adminId", "==", masterId), 
          where("kw", "==", Number(kw)), 
          where("year", "==", Number(year))
        );
        onSnapshot(qProjects, (snap) => setProjects(snap.docs.map(doc => ({ id: doc.id, ...doc.data() }))));

        // 3. Statusai
        const qStatuses = query(collection(db, "weeklyPlans"),
          where("adminId", "==", masterId),
          where("kw", "==", Number(kw)),
          where("year", "==", Number(year))
        );
        onSnapshot(qStatuses, (snap) => {
            const map = {};
            snap.docs.forEach(doc => {
                const data = doc.data();
                if (!map[data.workerId]) map[data.workerId] = {};
                map[data.workerId][data.dayIndex] = data;
            });
            setStatuses(map);
        });

        // 4. SVARBU: Atsisiunčiame TIKSLIUS duomenis
        const neededMonths = new Set();
        weekData.forEach(dayInfo => {
            const m = (dayInfo.dateObj.getMonth() + 1).toString().padStart(2, '0');
            const y = dayInfo.dateObj.getFullYear();
            neededMonths.add(`${y}_${m}`);
        });

        const detailsMap = {};
        await Promise.all(workersList.map(async (w) => {
            detailsMap[w.id] = {}; 
            await Promise.all(Array.from(neededMonths).map(async (ym) => {
                const docId = `${w.id}_${ym}`;
                try {
                    const snap = await getDoc(doc(db, "monthlyWorkHours", docId));
                    if (snap.exists()) {
                        const data = snap.data();
                        detailsMap[w.id][ym] = data.days || {}; 
                    }
                } catch (e) {
                    console.error("Klaida gaunant detales", e);
                }
            }));
        }));
        setDetailedWorkerData(detailsMap);
      }
    };
    loadData();
  }, [kw, year, currentUser, weekData]);

  // --- ATNAUJINTA: GRUPUOJA DIENAS SU NAUJA LOGIKA ---
  const getGroupedResource = (workerId, resourceType) => {
      const workerMonths = detailedWorkerData[workerId];
      if (!workerMonths) return "-";

      const groups = {}; 

      weekData.forEach((dayInfo, index) => {
          const dayName = DAYS_KEYS[index];
          const y = dayInfo.dateObj.getFullYear();
          const m = (dayInfo.dateObj.getMonth() + 1).toString().padStart(2, '0');
          const d = dayInfo.dateObj.getDate().toString();
          const keyYM = `${y}_${m}`;

          // Randame projektus šiai dienai
          const assignedProjects = projects.filter(p => 
             p.days?.[dayName]?.some(w => (typeof w === 'string' ? w === workerId : w.id === workerId))
          );

          if (assignedProjects.length > 0) {
              const dayOverrides = workerMonths[keyYM]?.[d] || {}; // Visa dienos info
              
              assignedProjects.forEach(proj => {
                  let val = "";
                  
                  // 1. Prioritetas: Dienos specifinis override (Projekto lygmenyje)
                  const specificOverride = dayOverrides[proj.id] || {};
                  
                  if (resourceType === 'hotel') val = specificOverride.hotel;
                  if (resourceType === 'transport') val = specificOverride.transport;
                  if (resourceType === 'phone') val = specificOverride.phone;

                  // 2. Prioritetas: Jei nėra override, imam bendrą projekto worker detail
                  if (!val && proj.workerDetails && proj.workerDetails[workerId]) {
                      if (resourceType === 'hotel') val = proj.workerDetails[workerId].accommodation;
                      if (resourceType === 'transport') val = proj.workerDetails[workerId].transport;
                      if (resourceType === 'phone') val = proj.workerDetails[workerId].phone;
                  }

                  // 3. Prioritetas: Bendras projekto info (jei dar nerado)
                  if (!val && proj.info) {
                      if (resourceType === 'hotel') val = proj.info.unterkunft;
                      if (resourceType === 'transport') val = proj.info.transport;
                      if (resourceType === 'phone') val = proj.info.tel;
                  }

                  if (val && val.trim() !== "" && val !== "-") {
                      if (!groups[val]) groups[val] = [];
                      if (!groups[val].includes(dayName)) groups[val].push(dayName);
                  }
              });
          }
      });

      const entries = Object.entries(groups);
      if (entries.length === 0) return "";

      return entries.map(([val, days]) => {
          if (days.length === 7) return val; 
          return `${val} (${days.join(", ")})`;
      }).join("; ");
  };

  // --- PAGALBINĖ F-JA GAUTI KOMENTARĄ ---
  const getProjectComment = (workerId, project, dayInfo) => {
      const y = dayInfo.dateObj.getFullYear();
      const m = (dayInfo.dateObj.getMonth() + 1).toString().padStart(2, '0');
      const d = dayInfo.dateObj.getDate().toString();
      const keyYM = `${y}_${m}`;

      const workerMonths = detailedWorkerData[workerId];
      if (!workerMonths) return project.info.kommentar || null;

      const dayOverrides = workerMonths[keyYM]?.[d] || {};
      const specificData = dayOverrides[project.id] || {};

      // Pirmumas: Specifinis dienos komentaras -> Projekto worker komentaras -> Bendras komentaras
      if (specificData.comment) return specificData.comment;
      if (project.workerDetails?.[workerId]?.comment) return project.workerDetails[workerId].comment;
      
      return project.info.kommentar || null;
  };

  const handleDownloadPDF = () => {
    const doc = new jsPDF('landscape');
    
    doc.setFontSize(16);
    doc.text(latinize(fullCompanyInfo.name.toUpperCase()), 14, 15);
    doc.setFontSize(9);
    doc.text(latinize(fullCompanyInfo.address), 14, 21);
    doc.text(`${latinize(t('admin'))}: ${latinize(fullCompanyInfo.admin)}`, 14, 26);
    
    doc.setFontSize(12);
    doc.text(`${latinize(t('weekly_plan').toUpperCase())} KW ${kw} / ${year} (${dateRange})`, 14, 35);

    const head = [[latinize(t('employees').toUpperCase()), ...weekData.map((d, i) => `${DAYS_KEYS[i]} (${d.formatted})`)]];
    
    const columnStyles = { 0: { cellWidth: 45 } };
    for(let i=1; i<=7; i++) columnStyles[i] = { cellWidth: 33 };

    const body = allWorkers.map(worker => {
      const transportStr = latinize(getGroupedResource(worker.id, 'transport'));
      const accommodationStr = latinize(getGroupedResource(worker.id, 'hotel'));
      const phoneStr = latinize(getGroupedResource(worker.id, 'phone'));
      
      let leftColText = `${latinize(worker.name)}`;
      if (transportStr) leftColText += `\n\n${latinize(t('car'))}:\n${transportStr}`;
      if (accommodationStr) leftColText += `\n\n${latinize(t('hotel'))}:\n${accommodationStr}`;
      if (phoneStr) leftColText += `\n\nTel:\n${phoneStr}`;

      const rowData = [
        { 
            content: leftColText, 
            rgbColor: null 
        }
      ];

      DAYS_KEYS.forEach((day, dayIndex) => {
        const dayInfo = weekData[dayIndex];
        const assignedProjects = projects.filter(p => (p.days?.[day] || []).some(w => (typeof w === 'object' ? w.id === worker.id : w === worker.id)));

        if (assignedProjects.length > 0) {
            const cellContent = assignedProjects.map(project => {
                const info = project.info;
                // ČIA NAUDOJAME NAUJĄ LOGIKĄ KOMENTARAMS
                const specificComment = getProjectComment(worker.id, project, dayInfo);

                return [
                    latinize(info.auftraggeber),
                    latinize(info.bauvorhaben),
                    latinize(info.adresse),
                    info.ansprechpartner ? `AP: ${latinize(info.ansprechpartner)}` : null,
                    info.kontakt ? `Tel: ${latinize(info.kontakt)}` : null,
                    specificComment ? `! ${latinize(specificComment)}` : null
                ].filter(line => line && line.trim() !== "").join("\n");
            }).join("\n\n----------------\n\n"); 

            const firstProjectInfo = assignedProjects[0].info;
            const uniqueKey = (firstProjectInfo.auftraggeber || "") + (firstProjectInfo.bauvorhaben || "");
            rowData.push({ content: cellContent, rgbColor: getPastelRgbArray(uniqueKey || "---") });
        } else {
            const statusData = statuses[worker.id]?.[dayIndex];
            if (statusData) {
                const rawStatus = statusData.status || "";
                const normalizedStatus = latinize(rawStatus).toLowerCase();
                
                let label = rawStatus;
                if(normalizedStatus.includes('atostog')) label = t('vacation');
                else if(normalizedStatus.includes('serg') || normalizedStatus.includes('krank')) label = t('sick');
                else if(normalizedStatus.includes('laisv')) label = t('off');
                else if(normalizedStatus.includes('svent')) label = t('holiday');

                const rgb = hexToRgbArray(statusData.color) || [240, 240, 240];
                rowData.push({ content: latinize(label.toUpperCase()), rgbColor: rgb });
            } else {
                rowData.push({ content: "-", rgbColor: null });
            }
        }
      });
      return rowData;
    });

    const totalPagesExp = "{total_pages_count_string}";

    autoTable(doc, {
      head: head,
      body: body,
      startY: 40,
      margin: { left: 7, right: 7, top: 40, bottom: 10 },
      styles: { fontSize: 7, cellPadding: 2, lineColor: [0, 0, 0], lineWidth: 0.1, textColor: [0, 0, 0], overflow: 'linebreak' },
      columnStyles: columnStyles,
      theme: 'grid',
      headStyles: { fillColor: [22, 27, 36], textColor: [255, 255, 255] },
      
      didParseCell: function (data) {
          if (data.section === 'body' && data.column.index > 0) {
              const cellData = data.cell.raw;
              if (cellData && cellData.rgbColor) data.cell.styles.fillColor = cellData.rgbColor;
          }
      },

      didDrawPage: function (data) {
          const pageLabel = latinize(t('page') || "Page");
          const ofLabel = latinize(t('of') || "/");

          let str = pageLabel + " " + doc.internal.getNumberOfPages();
          
          if (typeof doc.putTotalPages === 'function') {
              str = str + " " + ofLabel + " " + totalPagesExp;
          }
          
          doc.setFontSize(8);
          doc.setTextColor(40);
          const pageSize = doc.internal.pageSize;
          const pageHeight = pageSize.height ? pageSize.height : pageSize.getHeight();
          
          doc.text(str, data.settings.margin.left, pageHeight - 5);
      }
    });

    if (typeof doc.putTotalPages === 'function') {
        doc.putTotalPages(totalPagesExp);
    }

    doc.save(`Wochenplan_KW${kw}_${year}.pdf`);
  };

  return (
    <div className="export-full-bg">
      <div className="export-container">
        <button className="back-btn no-print" onClick={() => navigate("/admin/weeklyplan")}>
            <ArrowLeft size={18} style={{marginRight: 5}}/> {t('back')}
        </button>
        <div className="no-print logo-main-container"><img src={bfLogo} alt="BF Logo" className="bf-hero-logo" /></div>
        <div className="no-print export-nav-row">
          <div className="nav-side-empty"></div>
          <div className="kw-nav-luxury-wide">
              <ChevronLeft className="kw-nav-arrow" onClick={() => setKw(k => k - 1)} />
              <div className="kw-wide-info">
                <span className="kw-wide-nr">KW {kw}</span>
                <div className="kw-divider-vertical"></div>
                <span className="kw-wide-range">{dateRange}</span>
              </div>
              <ChevronRight className="kw-nav-arrow" onClick={() => setKw(k => k + 1)} />
          </div>
          <div className="nav-side-empty" style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button className="bf-green-print-btn" onClick={handleDownloadPDF}>
                <Download size={18} /> <span>PDF DOWNLOAD</span>
            </button>
          </div>
        </div>
        <div className="table-luxury-wrapper">
          <table className="luxury-table export-table">
            <thead>
              <tr>
                <th className="th-worker">{t('employees').toUpperCase()}</th>
                {weekData.map((d, i) => (<th key={i} className="th-day">{t(`days.${DAYS_KEYS[i]}`)}<br/><span>{d.formatted}</span></th>))}
              </tr>
            </thead>
            <tbody>
              {allWorkers.map((worker) => {
                // KAIRYS STULPELIS SU NAUJA LOGIKA
                const transportStr = getGroupedResource(worker.id, 'transport');
                const accommodationStr = getGroupedResource(worker.id, 'hotel');
                const phoneStr = getGroupedResource(worker.id, 'phone');

                return (
                <tr key={worker.id} className="team-row">
                  <td className="td-worker">
                    <div className="w-name-thin" style={{fontWeight: 'bold', fontSize: '13px', marginBottom: '10px'}}>{worker.name}</div>
                    <div className="info-block-left">
                        {transportStr && (
                            <div style={{ marginBottom: '8px' }}>
                                <div style={{ fontSize: '10px', fontWeight: 'bold', color: '#888' }}>{t('car')}:</div>
                                <div className="info-badge" style={{whiteSpace: 'pre-wrap'}}>{transportStr}</div>
                            </div>
                        )}
                        {accommodationStr && (
                            <div style={{ marginBottom: '8px' }}>
                                <div style={{ fontSize: '10px', fontWeight: 'bold', color: '#888' }}>{t('hotel')}:</div>
                                <div className="info-badge" style={{whiteSpace: 'pre-wrap'}}>{accommodationStr}</div>
                            </div>
                        )}
                        {phoneStr && (
                            <div>
                                <div style={{ fontSize: '10px', fontWeight: 'bold', color: '#888' }}>Tel:</div>
                                <div className="info-badge" style={{whiteSpace: 'pre-wrap'}}>{phoneStr}</div>
                            </div>
                        )}
                    </div>
                  </td>
                  {DAYS_KEYS.map((day, dayIndex) => {
                    const assignedProjects = projects.filter(p => (p.days?.[day] || []).some(w => (typeof w === 'object' ? w.id === worker.id : w === worker.id)));

                    if (assignedProjects.length > 0) {
                        return (
                            <td key={day} className="td-data">
                                {assignedProjects.map((project, idx) => {
                                    const uniqueKey = (project.info?.auftraggeber || "") + (project.info?.bauvorhaben || "");
                                    const rgb = getPastelRgbArray(uniqueKey || "---");
                                    
                                    // ČIA NAUDOJAME NAUJĄ LOGIKĄ KOMENTARAMS EKRANE
                                    const specificComment = getProjectComment(worker.id, project, weekData[dayIndex]);

                                    return (
                                        <div key={project.id} className="entry-card project-block" style={{ backgroundColor: rgb ? `rgb(${rgb[0]},${rgb[1]},${rgb[2]})` : '#333', color: '#000', marginBottom: '4px' }}>
                                          <div className="entry-client">{project.info?.auftraggeber}</div>
                                          <div className="entry-title">{project.info?.bauvorhaben}</div>
                                          <div className="proj-addr">{project.info?.adresse}</div>
                                          
                                          {project.info?.ansprechpartner && (
                                              <div style={{ fontSize: '9px', fontStyle: 'italic', marginTop: '2px', color: '#000' }}>
                                                  AP: {project.info.ansprechpartner}
                                              </div>
                                          )}
                                          {project.info?.kontakt && (
                                              <div style={{ fontSize: '9px', marginTop: '2px', color: '#000' }}>
                                                  Tel: {project.info.kontakt}
                                              </div>
                                          )}
                                          
                                          {/* RODOME SPECIFINĮ KOMENTARĄ JEI YRA, JEI NE - BENDRĄ */}
                                          {specificComment && (
                                              <div style={{ fontSize: '9px', fontWeight: 'bold', marginTop: '3px', color: '#d00' }}>
                                                  ! {specificComment}
                                              </div>
                                          )}
                                        </div>
                                    );
                                })}
                            </td>
                        );
                    } else {
                        const statusData = statuses[worker.id]?.[dayIndex];
                        if (statusData) {
                            const rawStatus = statusData.status || "";
                            const normalizedStatus = latinize(rawStatus).toLowerCase();
                            
                            let label = rawStatus;
                            if(normalizedStatus.includes('atostog')) label = t('vacation');
                            else if(normalizedStatus.includes('serg') || normalizedStatus.includes('krank')) label = t('sick');
                            else if(normalizedStatus.includes('laisv')) label = t('off');
                            else if(normalizedStatus.includes('svent')) label = t('holiday');

                            return (
                                  <td key={day} className="td-data">
                                      <div className="entry-card status-block" style={{ backgroundColor: statusData.color, color: '#fff', textAlign: 'center', fontWeight: 'bold', fontSize: '10px' }}>
                                          {label.toUpperCase()}
                                      </div>
                                  </td>
                             );
                        }
                        return <td key={day} className="td-data">-</td>;
                    }
                  })}
                </tr>
              )})}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}